import { FC, useEffect, useRef, useState } from 'react';
import { InputChangeEventDetail } from '@ionic/react';
import { AllieCheckbox, AllieInput, AllieNote, AllieText } from '../../../components';
import { AllieLabelStyled, AllieItemStyled, AllieListStyled } from './EmailInput.styles';
import { AllieEyeCloseIcon, AllieEyeOpenIcon } from '../../../styling/icons';
import { isMobile, isTablet } from '../../../common/utils';

interface EmailInputProps {
  disabled?: boolean;
  emailPlaceHolder?: string;
  passwordPlaceHolder?: string;
  invalidEmailText?: string;
  labelEmail?: string;
  labelPassword?: string;
  labelForgotPassword?: string;
  labelRememberUser?: string;
  messageInvalidEmail?: string;
  autoFillCredentials?: boolean;
  getCredentials?: () => { email: string; password: string } | null;
  onForgotPassword?: (email?: string) => void;
  onAutoFillCredentials?: (shouldStoreUser: boolean) => void;
  onChangeEmail?: (email: string, valid: boolean) => void;
  onChangePassword?: (password: string) => void;
}

export const EmailInput: FC<EmailInputProps> = ({
  disabled,
  emailPlaceHolder,
  passwordPlaceHolder,
  invalidEmailText,
  labelEmail,
  labelPassword,
  messageInvalidEmail,
  labelForgotPassword,
  labelRememberUser,
  autoFillCredentials,
  getCredentials,
  onAutoFillCredentials,
  onForgotPassword,
  onChangeEmail,
  onChangePassword,
}) => {
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const emailInputRef = useRef<HTMLIonInputElement>(null);
  const passwordInputRef = useRef<HTMLIonInputElement>(null);

  useEffect(() => {
    emailInputRef.current?.setFocus();
  }, [emailInputRef]);

  useEffect(() => {
    const fn = async () => {
      if(emailInputRef.current && passwordInputRef.current && !isTablet) {
        const credentials = getCredentials?.();
        if (credentials) {
          emailInputRef.current.value = credentials.email;
          passwordInputRef.current.value = credentials.password;

          if (credentials.email && credentials.password) {
            onChangeEmail && onChangeEmail(credentials.email, true);
            onChangePassword && onChangePassword(credentials.password);
          }
        }
      };
    }

    fn();
  }, [emailInputRef, passwordInputRef]);

  const checkEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  const handleEmailChange = (event: CustomEvent<InputChangeEventDetail>) => {
    const emailValue = event.detail.value!;

    const isValid = checkEmail(emailValue);

    setIsEmailValid(isValid);

    onChangeEmail && onChangeEmail(emailValue, isValid);
  };

  const handlePasswordChange = (event: CustomEvent<InputChangeEventDetail>) => {
    onChangePassword && onChangePassword(event.detail.value!);
  };

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const onForgotPasswordClick = () => {
    onForgotPassword && onForgotPassword(
      emailInputRef?.current?.value && isEmailValid ? `${emailInputRef.current.value}` : undefined
    );
  }

  const onAutoFillCredentialsChanged = () => onAutoFillCredentials?.(!autoFillCredentials);

  return (
    <AllieListStyled>
      <AllieItemStyled
        className={`email-item ${isEmailValid && 'ion-valid'} ${
          isEmailValid === false && 'ion-invalid'
        }`}
      >
        <AllieLabelStyled className="allie-text-button-normal" position="stacked">
          {labelEmail}
        </AllieLabelStyled>
        <AllieInput
          className="email-field"
          placeholder={emailPlaceHolder}
          type="email"
          allieType="primary"
          corner="rounded-edges"
          clearInput={true}
          disabled={disabled}
          onChange={handleEmailChange}
          allieSize="small"
          errorMessage={messageInvalidEmail}
          ref={emailInputRef}
        />
        <AllieNote slot="error">{invalidEmailText}</AllieNote>
      </AllieItemStyled>
      <AllieItemStyled>
        <AllieLabelStyled className="allie-text-button-normal" position="stacked">
          {labelPassword}
        </AllieLabelStyled>
        <AllieInput
          className="email-field"
          placeholder={passwordPlaceHolder}
          type={showPassword ? 'text' : 'password'}
          allieType="primary"
          corner="rounded-edges"
          disabled={disabled || !isEmailValid}
          clearInput={true}
          onChange={handlePasswordChange}
          allieSize="small"
          icon={showPassword ? <AllieEyeOpenIcon /> : <AllieEyeCloseIcon />}
          onClick={handleShowPassword}
          ref={passwordInputRef}
        />
      </AllieItemStyled>
      <AllieItemStyled style={{ marginTop: isMobile ? -10 : -20, width: '100%' }}>
        {!isTablet && (
          <div slot='start' style={{ display: 'inline-flex', alignItems: 'center' }}>
            <AllieCheckbox
              style={{ position: 'relative' }}
              value={autoFillCredentials}
              checked={autoFillCredentials}
              size={20}
              mode="md"
              onIonChange={() => onAutoFillCredentialsChanged()}
            />
            <AllieText
              type='body1'
              color='neutral-90'
              style={{ cursor: 'pointer', marginLeft: 10 }}
              onClick={onAutoFillCredentialsChanged}
            >
              {labelRememberUser}
            </AllieText>
          </div>
        )}
        <div slot='end' style={{ cursor: 'pointer' }} onClick={onForgotPasswordClick}>
          <AllieText type='body1' color='neutral-90'>
            {labelForgotPassword}
          </AllieText>
        </div>
      </AllieItemStyled>
    </AllieListStyled>
  );
};
